<template>
	<BurnUpChartBase
		class="burn-up-chart-revenue__chart"
		data-testid="burn-up-chart-revenue"
		:project-id="projectId"
		:retainer-period-id="retainerPeriodId"
		:loading="loading"
		:project="project"
		:make-y-axis-duration-options="yAxisDurationRevenue"
		:is-viewed-in-grid="isViewedInGrid"
		:has-sidebar="true"
		:budget-plot-lines="revenueBudgetPlotLines"
		:side-bar-topics="sidebarTopics"
		:plot-lines="plotLines"
		:gradient-lines="gradientLines"
		:tooltip-options="tooltipOptions"
		:progress="progress"
		:max-axis-value="maxAxisValue"
		:seconds-per-tick="secondsPerTick"
	/>
</template>
<script lang="ts" setup>
import { computed, reactive, toRefs, watch } from 'vue';
import BurnUpChartBase from './BurnUpChartBase.vue';
import {
	type TooltipLine,
	tooltipShared,
	yAxisDurationRevenue,
} from '@/lib/highchartsUtils';
import {
	type BurnUpChartQueryVariables,
	type Project,
	ProjectProgressGroupBy,
	type RetainerPeriod,
	useBurnUpChartQuery,
} from '@/generated/graphql';
import { useRefetchOnSocketMessage } from '@/lib/apollo/apolloTools';
import {
	APOLLO_PARAMS_PERSISTED_QUERY,
	usePersistedQueryLoading,
} from '@/lib/apollo/apollo';
import {
	useBooleanSetting,
	useSettings,
} from '@/composables/settings/useSettings';
import { Setting } from '@/constants/settings';
import {
	MARKER_RADIUS,
	BurnUpChartLineNameTranslations,
	TOLERANCE_THRESHOLD,
	DAY_INTERVAL_MS,
	NULL_VALUE_TEXT,
} from '../constants';
import {
	type ChartProgressGroup,
	type ChartProject,
	ChartZoneClasses,
} from '../types';
import { useFormatter, usePermissions, useTranslation } from '@/composables';
import {
	getEndMarkerTime,
	getForecastedStartTime,
	getFormattedProgressData,
	getIsTodayAfterProgress,
	getIsTodayBeforeProgress,
	getStartMarkerTime,
	getTodaysDateIndex,
	getYValues,
	getLastYValue,
} from '../helpers';
import { SuiSummaryTopicValueState } from '@developers/scoro-ui';
import { Permission, PermissionAction } from '@/constants/permissions';
import { SOCKET_MESSAGES_THAT_TRIGGER_REFETCHING } from '@/features/Project/constants';

const props = defineProps<{
	projectId: Project['id'];
	retainerPeriodId?: RetainerPeriod['id'];
	isViewedInGrid?: boolean;
}>();

const { projectId, retainerPeriodId } = toRefs(props);

const variables = reactive<BurnUpChartQueryVariables>({
	projectInput: {
		projectId: Number(projectId.value),
	},
	projectProgressInput: {
		groupBy: ProjectProgressGroupBy.Days,
		retainerPeriodId: retainerPeriodId.value,
	},
});

const { result, loading } = useRefetchOnSocketMessage(
	usePersistedQueryLoading(
		useBurnUpChartQuery(variables, APOLLO_PARAMS_PERSISTED_QUERY)
	),
	{
		messages: SOCKET_MESSAGES_THAT_TRIGGER_REFETCHING,
		filter: (message) =>
			String(message.data.project_id) === props.projectId,
	}
);

watch(retainerPeriodId, (retainerPeriodId) => {
	variables.projectProgressInput.retainerPeriodId = retainerPeriodId;
});

const { setting } = useSettings();

const displayCurrency = computed(() => {
	return setting(Setting.DISPLAY_CURRENCY);
});

const project = computed<ChartProject | undefined>(() => {
	if (result?.value?.Project) {
		return {
			...result.value.Project,
			progressData: (result.value.Project.progressData || []).map(
				(elem): ChartProgressGroup => ({
					budgetValue: elem.monetaryBudget,
					groupName: elem.groupName,
					plannedValue: 0,
					actualValue: elem.actualRevenue,
					forecastedValue: elem.forecastedRevenue,
					actualCost: elem.actualCost,
					actualRevenue: elem.actualRevenue,
					bookingsCost: elem.bookingsCost,
					costBudget: elem.costBudget,
					forecastedCost: elem.forecastedCost,
					forecastedRevenue: elem.forecastedRevenue,
					monetaryBudget: elem.monetaryBudget,
					tasksCost: elem.tasksCost,
				})
			),
		};
	}

	return undefined;
});

const progress = computed(() => getFormattedProgressData(project.value));

const isTodayBeforeProgress = computed(() =>
	getIsTodayBeforeProgress(progress.value)
);
const todaysDateIndex = computed(() => getTodaysDateIndex(progress.value));
const isTodayAfterProgress = computed(() =>
	getIsTodayAfterProgress(progress.value)
);
const forecastedStartTime = computed(() =>
	getForecastedStartTime(progress.value)
);
const startMarkerTime = computed(() =>
	getStartMarkerTime(project.value, progress.value, props.retainerPeriodId)
);
const endMarkerTime = computed(() =>
	getEndMarkerTime(project.value, progress.value, props.retainerPeriodId)
);

const { t } = useTranslation();
const { formatDateShortNoCurrentYear, formatMoney, formatNumber } =
	useFormatter();
const { can } = usePermissions();

const makeTooltipFormatterRevenue = () => (amount: number) => {
	return formatMoney(amount);
};

const [isLaborCostEnabled] = useBooleanSetting(Setting.SHOW_EVENT_PRICE);

const canSeeOtherLaborCost = computed(() => {
	return can(
		PermissionAction.HAS_PERMISSION,
		Permission.CAN_SEE_OTHER_LABORCOST
	);
});

const canSeeMarginAndMarkup = computed(() => {
	return can(
		PermissionAction.HAS_PERMISSION,
		Permission.CAN_SEE_MARGIN_AND_MARKUP
	);
});

const canSeeCostAndProfitBreakdown = computed(() => {
	return (
		canSeeMarginAndMarkup.value &&
		(canSeeOtherLaborCost.value ||
			(!isLaborCostEnabled.value && !canSeeOtherLaborCost.value))
	);
});

const budget = computed(() => {
	if (progress.value) {
		return progress.value.at(1)?.budgetValue;
	}
	return undefined;
});

const costBudget = computed(() => {
	if (progress.value) {
		return progress.value.at(1)?.costBudget;
	}
	return undefined;
});

const isRevenueBudgetSet = computed(() => !!budget.value);
const isCostBudgetSet = computed(() => !!costBudget.value);

const actualValues = computed(() => {
	if (isTodayBeforeProgress.value) {
		return [];
	}
	let sum = 0;
	return progress.value
		.slice(0, todaysDateIndex.value ? todaysDateIndex.value + 1 : undefined)
		.map((progressGroup, index) => {
			if (index === todaysDateIndex.value) {
				sum += progressGroup.forecastedValue;
			}
			sum += progressGroup.actualValue;
			return sum;
		});
});

const actualCostValues = computed(() => {
	if (isTodayBeforeProgress.value) {
		return [];
	}
	let sum = 0;
	return progress.value
		.slice(0, todaysDateIndex.value ? todaysDateIndex.value + 1 : undefined)
		.map((progressGroup, index) => {
			if (index === todaysDateIndex.value) {
				sum += progressGroup.forecastedCost;
			}
			sum += progressGroup.actualCost;
			return sum;
		});
});

const forecastedCostValues = computed(() => {
	if (isTodayAfterProgress.value) {
		return [];
	}

	const endIndexActual = isTodayBeforeProgress.value
		? 0
		: todaysDateIndex.value + 1;
	const startIndexForecasted = isTodayBeforeProgress.value
		? 0
		: todaysDateIndex.value;
	let sum = progress.value
		.slice(0, endIndexActual)
		.reduce((acc, curr) => acc + curr.actualCost, 0);

	return progress.value
		.slice(startIndexForecasted)
		.map((progressGroup, i) => {
			sum += progressGroup.forecastedCost;
			return {
				x: forecastedStartTime.value + i * DAY_INTERVAL_MS,
				y: sum,
			};
		});
});

const forecastedValues = computed(() => {
	if (isTodayAfterProgress.value) {
		return [];
	}

	const endIndexActual = isTodayBeforeProgress.value
		? 0
		: todaysDateIndex.value + 1;
	const startIndexForecasted = isTodayBeforeProgress.value
		? 0
		: todaysDateIndex.value;
	let sum = progress.value
		.slice(0, endIndexActual)
		.reduce((acc, curr) => acc + curr.actualValue, 0);

	return progress.value
		.slice(startIndexForecasted)
		.map((progressGroup, i) => {
			sum += progressGroup.forecastedValue;
			return {
				x: forecastedStartTime.value + i * DAY_INTERVAL_MS,
				y: sum,
			};
		});
});

const lastForecastedRevenueValue = computed(() =>
	getLastYValue(forecastedValues.value)
);
const lastForecastedCostValue = computed(() =>
	getLastYValue(forecastedCostValues.value)
);

const lastActualValue = computed(
	() => actualValues.value[actualValues.value.length - 1]
);
const lastActualCostValue = computed(
	() => actualCostValues.value[actualCostValues.value.length - 1]
);

const profit = computed(() => {
	if (
		isTodayAfterProgress.value &&
		!lastForecastedCostValue.value &&
		!lastForecastedRevenueValue.value
	) {
		// if we are looking at a project that has ended and all data is in the past (no forecasted values),
		// then the profit is calculated from actual values, but depends on if budget is set or not.
		// if budget is set, then the budget is the hard limit and profit = budget - actual cost.
		if (budget.value && lastActualValue.value > budget.value) {
			return budget.value - lastActualCostValue.value;
		}
		// if actual value is lower than budget, or budget is not set, then the profit is difference of actual values.
		return lastActualValue.value - lastActualCostValue.value;
	}

	// here we deal with project data that also has forecasted values as well.
	if (budget.value) {
		if (costBudget.value) {
			// with cost budgets, profit depends on if forecasted cost exceeds cost budget or not
			// If there is data that forecasted cost will be higher than cost budget,
			// then we can say with certainty, that the forecasted profit will be less than budgeted.

			if ((lastForecastedCostValue.value ?? 0) < costBudget.value) {
				return budget.value - costBudget.value;
			}
			return budget.value - (lastForecastedCostValue.value ?? 0);
		}
		// without cost budget, we can only calculate profit from forecasted cost value and compared it with budget.
		return budget.value - (lastForecastedCostValue.value ?? 0);
	}
	if (costBudget.value) {
		// if revenue budget is not set, but the cost budget is set.
		// Then profit is difference of forecasted revenue and cost budget or forecasted cost - depending on which cost value is larger.
		if (costBudget.value > (lastForecastedCostValue.value ?? 0)) {
			return (lastForecastedRevenueValue.value ?? 0) - costBudget.value;
		}
	}
	// without any budgets, we compare only forecasted values.
	return (
		(lastForecastedRevenueValue.value ?? 0) -
		(lastForecastedCostValue.value ?? 0)
	);
});

const gradient = computed(() => {
	let top = undefined;
	let bottom = undefined;

	if (profit.value <= 0) {
		return { top, bottom };
	}

	if (budget.value) {
		if (
			isTodayAfterProgress.value &&
			budget.value > lastActualValue.value
		) {
			top = lastActualValue.value;
			bottom = lastActualValue.value - profit.value;
		} else {
			top = budget.value;
			bottom = budget.value - profit.value;
		}
	} else {
		top = lastForecastedRevenueValue.value ?? lastActualValue.value;
		bottom =
			(lastForecastedRevenueValue.value ?? lastActualValue.value) -
			profit.value;
	}
	return { top, bottom };
});

const actualBudgetBurn = computed(() => {
	if (isTodayBeforeProgress.value) {
		return 0;
	}

	if (isTodayAfterProgress.value) {
		return progress.value.reduce((acc, curr) => {
			return acc + curr.actualValue;
		}, 0);
	}

	return progress.value
		.slice(0, (todaysDateIndex.value || 0) + 1)
		.reduce((acc, curr) => acc + curr.actualValue, 0);
});

const actualCostBudgetBurn = computed(() => {
	if (isTodayBeforeProgress.value) {
		return 0;
	}

	if (isTodayAfterProgress.value) {
		return progress.value.reduce((acc, curr) => {
			return acc + curr.actualCost;
		}, 0);
	}

	return progress.value
		.slice(0, (todaysDateIndex.value || 0) + 1)
		.reduce((acc, curr) => acc + curr.actualCost, 0);
});

const maxAxisValue = computed(() => {
	const minAxisSize = 1000;
	return Math.max(
		minAxisSize,
		budget.value ?? 0,
		costBudget.value ?? 0,
		...actualValues.value,
		...getYValues(forecastedValues.value),
		...actualCostValues.value,
		...getYValues(forecastedCostValues.value)
	);
});

const secondsPerTick = computed<number>(() => {
	const step = maxAxisValue.value / 6;
	return step < 1000 ? 1000 : Math.round(step * 0.001) * 1000;
});

const budgetData = computed(() => {
	const array = [];
	for (let i = 0; i < progress.value.length; i++) {
		array.push(budget.value);
	}
	return array;
});

const costBudgetData = computed(() => {
	const array = [];
	for (let i = 0; i < progress.value.length; i++) {
		array.push(costBudget.value);
	}
	return array;
});

const revenueBudgetPlotLines = computed(() => {
	return [
		...(isCostBudgetSet.value && canSeeCostAndProfitBreakdown.value
			? [
					{
						id: 'project_burn_chart_total_budgeted_cost',
						value: costBudget.value,
						zIndex: 4,
						label: {
							y: -8,
							x: 15,
							formatter() {
								return `<div style="color: #50248F; font-weight: 600" > ${t(
									'project_burn_chart_total_budgeted_cost'
								)}</div>`;
							},
						},
						className: 'budget-line-cost',
					},
				]
			: []),
		...(isRevenueBudgetSet.value
			? [
					{
						id: 'project_burn_up_chart_budget',
						value: budget.value,
						zIndex: 4,
						label: {
							y: -8,
							x: 15,
							formatter() {
								return `<div style="color: #00848E; font-weight: 600" >${t(
									'project_burn_up_chart_budget'
								)}</div>`;
							},
						},
						className: 'budget-line-revenue',
					},
				]
			: []),
	] as Highcharts.YAxisPlotLinesOptions[];
});

const plotLines = computed(() => {
	return [
		{
			name: BurnUpChartLineNameTranslations.Budget,
			data: isRevenueBudgetSet.value ? budgetData.value : [],
			type: 'spline',
			showInLegend: isRevenueBudgetSet.value,
			legendSymbol: 'lineMarker',
			legendIndex: 1,
			zIndex: 3,
			marker: {
				symbol: 'circle',
				radius: MARKER_RADIUS,
			},
			states: {
				hover: {
					halo: null,
				},
			},
		},

		{
			name: BurnUpChartLineNameTranslations.UsedBudget,
			type: 'spline',
			data: actualValues.value,
			zones: budget.value
				? [
						{ value: budget.value + TOLERANCE_THRESHOLD + 5 },
						{ className: ChartZoneClasses.OverActual },
					]
				: undefined,
			zoneAxis: 'y',
			marker: {
				symbol: 'circle',
				radius: MARKER_RADIUS,
			},
			states: {
				hover: {
					halo: null,
				},
			},
			legendSymbol: 'lineMarker',
			legendIndex: 2,
			zIndex: 10,
		},

		{
			name: BurnUpChartLineNameTranslations.ForecastedBudgetBurn,
			type: 'spline',
			data: forecastedValues.value,
			zones: budget.value
				? [
						{ value: budget.value + TOLERANCE_THRESHOLD + 5 },
						{ className: ChartZoneClasses.OverActual },
					]
				: undefined,
			zoneAxis: 'y',
			marker: {
				symbol: 'circle',
				radius: MARKER_RADIUS,
			},
			states: {
				hover: {
					halo: null,
				},
			},
			legendSymbol: 'lineMarker',
			legendIndex: 3,
			zIndex: 10,
		},

		{
			name: 'project_timeframe',
			showInLegend: false,
			type: 'spline',
			zIndex: 10,
			data: [
				startMarkerTime.value
					? {
							x: startMarkerTime.value,
							y: 0,
							marker: {
								enabled: true,
								symbol: 'projectStart',
								states: {
									hover: {
										enabled: false,
									},
								},
							},
						}
					: undefined,
				endMarkerTime.value
					? {
							x: endMarkerTime.value,
							y: 0,
							marker: {
								enabled: true,
								symbol: 'projectEnd',
								states: {
									hover: {
										enabled: false,
									},
								},
							},
						}
					: undefined,
			].filter(Boolean),
			states: {
				hover: {
					halo: null,
				},
			},
		},

		...(canSeeCostAndProfitBreakdown.value
			? [
					{
						name: BurnUpChartLineNameTranslations.ActualCost,
						type: 'spline',
						data: actualCostValues.value,
						zones: costBudget.value
							? [
									{
										value:
											costBudget.value +
											TOLERANCE_THRESHOLD +
											5,
									},
									{
										className: ChartZoneClasses.OverActual,
									},
								]
							: undefined,
						zoneAxis: 'y',
						marker: {
							symbol: 'circle',
							radius: MARKER_RADIUS,
						},
						states: {
							hover: {
								halo: null,
							},
						},
						legendSymbol: 'lineMarker',
						legendIndex: 5,
						zIndex: 10,
					},

					{
						name: BurnUpChartLineNameTranslations.ForecastedCostBurn,
						type: 'spline',
						data: forecastedCostValues.value,
						zones: costBudget.value
							? [
									{
										value:
											costBudget.value +
											TOLERANCE_THRESHOLD +
											5,
									},
									{
										className: ChartZoneClasses.OverActual,
									},
								]
							: undefined,
						zoneAxis: 'y',
						marker: {
							symbol: 'circle',
							radius: MARKER_RADIUS,
						},
						states: {
							hover: {
								halo: null,
							},
						},
						legendSymbol: 'lineMarker',
						legendIndex: 6,
						zIndex: 10,
					},
					{
						name: BurnUpChartLineNameTranslations.TotalCost,
						data: isCostBudgetSet.value ? costBudgetData.value : [],
						type: 'spline',
						showInLegend: isCostBudgetSet.value,
						legendSymbol: 'lineMarker',
						legendIndex: 4,
						zIndex: 3,
						marker: {
							symbol: 'circle',
							radius: MARKER_RADIUS,
						},
						states: {
							hover: {
								halo: null,
							},
						},
					},
				]
			: []),
		...(canSeeCostAndProfitBreakdown.value
			? [
					{
						className: 'project_burn_up_profit_shadow',
						showInLegend: profit.value,
						legendIndex: 7,
						name: 'project_burn_up_chart_forecasted_profit',
						type: 'arearange',
						zIndex: 2,
						marker: {
							enabled: false,
							symbol: 'circle',
							radius: 0,
						},
						states: {
							hover: {
								halo: null,
							},
						},
						data: (progress.value || []).map(() => [
							gradient.value.top ?? 0,
							gradient.value.bottom ?? 0,
						]),
					},
				]
			: [
					{
						className: 'project_burn_up_budget_shadow',
						showInLegend: false,
						name: 'project_burn_up_budget_shadow',
						type: 'arearange',
						zIndex: 2,
						marker: {
							enabled: false,
							symbol: 'triangle',
							radius: 0,
						},
						states: {
							hover: {
								halo: null,
							},
						},
						data: (progress.value || []).map(() => [
							gradient.value.top ?? 0,
							gradient.value.bottom ?? 0,
						]),
					},
				]),
	] as Highcharts.SeriesOptionsType[];
});

const gradientLines = computed(() => {
	return {
		gradient0: budget.value
			? {
					tagName: 'linearGradient',
					id: 'burn-up-revenue-gradient-0',
					x1: 0,
					y1: 0,
					x2: 0,
					y2: 1,
					children: [
						{
							tagName: 'stop',
							offset: 0,
						},
						{
							tagName: 'stop',
							offset: 1,
						},
					],
				}
			: {},
		gradient1: {
			tagName: 'linearGradient',
			id: 'burn-up-revenue-gradient-1',
			x1: 0,
			y1: 0,
			x2: 0,
			y2: 1,
			children: [
				{
					tagName: 'stop',
					offset: 0,
				},
				{
					tagName: 'stop',
					offset: 1,
				},
			],
		},
	} as Highcharts.DefsOptions;
});

const tooltipOptions = computed(() => {
	return tooltipShared({
		getTitle: (x) => formatDateShortNoCurrentYear(new Date(x as number)),
		getLinesToShow(pointsByName): (TooltipLine | undefined)[] {
			const format = makeTooltipFormatterRevenue();

			const forecast =
				pointsByName.project_burn_up_chart_forecasted_budget_burn?.y;

			const actual = pointsByName.budget_burn_chart_used_budget?.y;

			const showForecast = forecast !== undefined;

			const showActual =
				actual !== undefined && actual !== null && !showForecast;

			const getSecondaryValue = (amount: number) => {
				return budget.value &&
					amount > budget.value + TOLERANCE_THRESHOLD
					? `+${format(amount - budget.value)}`
					: undefined;
			};

			const forecastCost =
				pointsByName.project_burn_up_chart_forecasted_cost_burn?.y;

			const actualCost =
				pointsByName.project_burn_up_chart_actual_cost?.y;

			const showForecastCost = forecastCost !== undefined;

			const showActualCost =
				actualCost !== undefined &&
				actualCost !== null &&
				!showForecastCost;

			const getSecondaryCostValue = (amount: number) => {
				return costBudget.value &&
					amount > costBudget.value + TOLERANCE_THRESHOLD
					? `+${format(amount - costBudget.value)}`
					: undefined;
			};

			return [
				budget.value
					? {
							label: t('project_burn_chart_total_budget'),
							value: format(budget.value),
							colorIndex: 0,
							type: 'line',
						}
					: undefined,
				showActual
					? {
							label: t('budget_burn_chart_used_budget'),
							value: format(actual ?? 0),
							secondaryValue: getSecondaryValue(actual ?? 0),
							colorIndex: 1,
							type: 'line',
						}
					: undefined,
				showForecast
					? {
							label: t('project_burn_up_chart_budget_burn'),
							value: format(forecast ?? 0),
							secondaryValue: getSecondaryValue(forecast ?? 0),
							colorIndex: 2,
							type: 'line',
						}
					: undefined,
				costBudget.value && canSeeCostAndProfitBreakdown.value
					? {
							label: t('project_burn_chart_total_budgeted_cost'),
							value: format(costBudget.value),
							colorIndex: 6,
							type: 'line',
						}
					: undefined,
				canSeeCostAndProfitBreakdown.value && showActualCost
					? {
							label: t('project_burn_up_chart_actual_cost'),
							value: format(actualCost ?? 0),
							secondaryValue: getSecondaryCostValue(
								actualCost ?? 0
							),
							colorIndex: 4,
							type: 'line',
						}
					: undefined,
				canSeeCostAndProfitBreakdown.value && showForecastCost
					? {
							label: t('project_burn_up_chart_cost_burn'),
							value: format(forecastCost ?? 0),
							secondaryValue: getSecondaryCostValue(
								forecastCost ?? 0
							),
							colorIndex: 5,
							type: 'line',
						}
					: undefined,
				canSeeCostAndProfitBreakdown.value && !!profit.value
					? {
							label: profit.value
								? t('project_burn_up_chart_forecasted_profit')
								: '',
							value: format(profit.value),
							colorIndex: profit.value,
							type: 'gradient',
						}
					: undefined,
			];
		},
	});
});

const forecastedValue = computed(
	() => forecastedValues.value[forecastedValues.value.length - 1]?.y ?? null
);
const forecastedCostValue = computed(
	() =>
		forecastedCostValues.value[forecastedCostValues.value.length - 1]?.y ??
		null
);
const profitPercentage = computed(() =>
	// profit depends on if there are any budgets set and if actual/forecasted values exceed budgets or not.
	// cost values can exceed cost budget, and then it affects the profit.
	// "actual revenue" value can exceed the budget, but it does not affect profit - this is because when budget has been set, then it is a "fixed budget" type project ->
	// -> and the "actual revenue" is an indication of what could have been earned (price * ete.duration), not what was actually earned (the word "actual" is misleading).
	// The fixed budget is what should be actually earned, unless the "actual revenue" remains lower than budget in the end and no more work is done.
	// "actual revenue" does not include invoices so it is not an indication of what is actually earned through invoices.
	// However, if budget is not set, then it is a "time and material" type project where whatever is earned is actual revenue in the end and there is no limit (no budget).
	//
	!!budget.value
		? (profit.value / budget.value) * 100
		: !!forecastedValue.value
			? (profit.value / forecastedValue.value) * 100
			: !!actualBudgetBurn.value
				? (profit.value / actualBudgetBurn.value) * 100
				: 0
);

const budgetedProfit = computed(
	() => (budget.value ?? 0) - (costBudget.value ?? 0)
);
const budgetedProfitPercentage = computed(() =>
	!!budget.value ? (budgetedProfit.value / budget.value) * 100 : 0
);
const revenueCardSecondaryUnit = computed(() =>
	isRevenueBudgetSet.value ? '%' : ''
);
const costCardSecondaryUnit = computed(() =>
	isCostBudgetSet.value ? '%' : ''
);
const profitCardSecondaryUnit = computed(() =>
	isRevenueBudgetSet.value && isCostBudgetSet.value ? '%' : ''
);
const revenueCardSecondaryDescription = computed(() =>
	isRevenueBudgetSet.value ? t('of_total_budget') : ''
);
const costCardSecondaryDescription = computed(() =>
	isCostBudgetSet.value ? t('of_budgeted_cost') : ''
);
const revenueCardIcon = 'icon-paper-coins-16';
const costCardIcon = 'icon-coins-16';
const profitCardIcon = 'icon-percent-16';

const isForecastedValueOverBudget = computed(
	() =>
		forecastedValue.value >
		(!!budget.value ? budget.value : forecastedValue.value) +
			TOLERANCE_THRESHOLD
);

const isForecastedCostValueOverBudget = computed(
	() =>
		forecastedCostValue.value >
		(!!costBudget.value ? costBudget.value : (forecastedValue.value ?? 0)) +
			TOLERANCE_THRESHOLD
);

const isActualBudgetBurnOverBudget = computed(
	() =>
		actualBudgetBurn.value >
		(!!budget.value ? budget.value : actualBudgetBurn.value) +
			TOLERANCE_THRESHOLD
);

const isActualCostBudgetBurnOverCostBudget = computed(
	() =>
		actualCostBudgetBurn.value >
		(!!costBudget.value ? costBudget.value : actualCostBudgetBurn.value) +
			TOLERANCE_THRESHOLD
);

const isActualCostBudgetBurnOverBudget = computed(
	() =>
		actualCostBudgetBurn.value >
		(!!budget.value ? budget.value : actualBudgetBurn.value) +
			TOLERANCE_THRESHOLD
);

const isForecastedCostOverCostBudget = computed(
	() =>
		forecastedCostValue.value >
		(!!costBudget.value ? costBudget.value : forecastedValue.value) +
			TOLERANCE_THRESHOLD
);

const sidebarTopics = computed(() => {
	return [
		{
			heading: t('budget_burn_chart_used_budget'),
			value: formatMoney(actualBudgetBurn.value ?? 0),
			unit: displayCurrency.value,
			valueState: isRevenueBudgetSet.value
				? isActualBudgetBurnOverBudget.value
					? SuiSummaryTopicValueState.DANGER
					: undefined
				: undefined,
			secondaryValue: isRevenueBudgetSet.value // For the percentage, we compare with budget, if the budget is set. If there is no budget, then there is nothing to compare.
				? formatNumber(
						budget.value && actualBudgetBurn.value
							? (actualBudgetBurn.value / budget.value) * 100
							: 0,
						{
							decimalPrecision: 0,
						}
					)
				: '',
			secondaryUnit: revenueCardSecondaryUnit.value,
			secondaryDescription: revenueCardSecondaryDescription.value,
			secondaryValueState: isRevenueBudgetSet.value
				? isActualBudgetBurnOverBudget.value
					? SuiSummaryTopicValueState.DANGER
					: undefined
				: undefined,
			iconName: revenueCardIcon,
		},
		{
			heading: t('project_burn_chart_forecasted_budget_burn'),
			value:
				forecastedValue.value === null
					? NULL_VALUE_TEXT
					: formatMoney(forecastedValue.value), // forecastedValue is the sum of forecasted values + actual values. It's the end of the line on chart. last element of the forecastedValues array.
			unit: forecastedValue.value === null ? '' : displayCurrency.value,
			valueState: isForecastedValueOverBudget.value
				? SuiSummaryTopicValueState.DANGER
				: undefined,
			secondaryValue:
				isRevenueBudgetSet.value && forecastedValue.value !== null // For the percentage, we compare with budget, if the budget is set. If there is no budget, then there is nothing to compare.
					? formatNumber(
							budget.value && forecastedValue.value
								? (forecastedValue.value / budget.value) * 100
								: 0,
							{
								decimalPrecision: 0,
							}
						)
					: '',
			secondaryUnit: revenueCardSecondaryUnit.value,
			secondaryDescription: revenueCardSecondaryDescription.value,
			secondaryValueState: isRevenueBudgetSet.value
				? isForecastedValueOverBudget.value
					? SuiSummaryTopicValueState.DANGER
					: undefined
				: undefined,
			iconName: revenueCardIcon,
		},
		...(canSeeCostAndProfitBreakdown.value
			? [
					{
						heading: t('project_burn_up_chart_actual_cost'), // actualCost is the sum of all actual cost values in the progress data until today (included).
						value: formatMoney(actualCostBudgetBurn.value),
						unit: displayCurrency.value,
						valueState: isCostBudgetSet.value
							? isActualCostBudgetBurnOverCostBudget.value
								? SuiSummaryTopicValueState.DANGER
								: undefined
							: isActualCostBudgetBurnOverBudget.value
								? SuiSummaryTopicValueState.DANGER
								: undefined,
						secondaryValue: isCostBudgetSet.value // For the percentage, we compare with budget, if the budget is set. If there is no budget, then there is nothing to compare.
							? formatNumber(
									costBudget.value &&
										actualCostBudgetBurn.value
										? (actualCostBudgetBurn.value /
												costBudget.value) *
												100
										: 0,
									{
										decimalPrecision: 0,
									}
								)
							: '',
						secondaryUnit: costCardSecondaryUnit.value,
						secondaryDescription:
							costCardSecondaryDescription.value,
						secondaryValueState: isCostBudgetSet.value
							? isActualCostBudgetBurnOverCostBudget.value
								? SuiSummaryTopicValueState.DANGER
								: undefined
							: undefined,
						iconName: costCardIcon,
					},
					{
						heading: t('project_burn_chart_forecasted_cost_burn'), // forecastedCostValue is the sum of forecasted cost values + actual cost values. It's the end of the line on chart. last element of the forecastedCostValues array.
						value:
							forecastedCostValue.value === null
								? NULL_VALUE_TEXT
								: formatMoney(forecastedCostValue.value),
						unit:
							forecastedCostValue.value === null
								? ''
								: displayCurrency.value,
						valueState: isCostBudgetSet.value
							? isForecastedCostValueOverBudget.value
								? SuiSummaryTopicValueState.DANGER
								: undefined
							: isForecastedCostOverCostBudget.value
								? SuiSummaryTopicValueState.DANGER
								: undefined,
						secondaryValue:
							isCostBudgetSet.value &&
							forecastedCostValue.value !== null // For the percentage, we compare with budget, if the budget is set. If there is no budget, then there is nothing to compare.
								? formatNumber(
										costBudget.value &&
											forecastedCostValue.value
											? (forecastedCostValue.value /
													costBudget.value) *
													100
											: 0,
										{
											decimalPrecision: 0,
										}
									)
								: '',
						secondaryUnit: costCardSecondaryUnit.value,
						secondaryDescription:
							costCardSecondaryDescription.value,
						secondaryValueState: isCostBudgetSet.value
							? isForecastedCostValueOverBudget.value
								? SuiSummaryTopicValueState.DANGER
								: undefined
							: undefined,
						iconName: costCardIcon,
					},
					{
						heading: t('forecasted_profit_margin'), // Lots of conditions to get the profit percentage. See comments where profitPercentage and Profit are calculated.
						value: formatMoney(profitPercentage.value),
						unit: '%',
						valueState:
							parseFloat(profitPercentage.value.toFixed(2)) <
							parseFloat(
								budgetedProfitPercentage.value.toFixed(2)
							)
								? SuiSummaryTopicValueState.DANGER
								: undefined,
						secondaryValue:
							isRevenueBudgetSet.value && isCostBudgetSet.value
								? formatNumber(budgetedProfitPercentage.value, {
										decimalPrecision: 0,
									})
								: '',
						secondaryUnit: profitCardSecondaryUnit.value,
						secondaryDescription: t(
							'budget_burn_chart_forecasted_vs_budgeted'
						),
						secondaryValueState: SuiSummaryTopicValueState.DEFAULT,
						isSecondaryValueFirst: false,
						iconName: profitCardIcon,
					},
				]
			: []),
	];
});
</script>
<style lang="scss">
.burn-up-chart-revenue {
	&__chart {
		--highcharts-color-0: #{$teal-60};
		--highcharts-color-1: #{$teal-40};
		--highcharts-color-2: #{$teal-40};
		--highcharts-color-3: #{$blue-50};
		--highcharts-color-4: #{$purple-40};
		--highcharts-color-5: #{$purple-40};
		--highcharts-color-6: #{$purple-60};
		--highcharts-color-7: #{$teal-40};
		--highcharts-graph-stroke-width: 2px;
		--highcharts-plot-line-stroke: var(--highcharts-color-0);
		--highcharts-plot-line-stroke-width: 1px;
		--highcharts-legend-item-cursor: default;

		&--hover {
			--highcharts-series-inactive-opacity: 1;
		}

		.highcharts-markers .highcharts-point {
			fill: $neutral-00;
			stroke-width: 2.5;
			stroke-dasharray: none;
		}

		.highcharts-series-0 {
			--highcharts-graph-stroke-width: 1px;
		}

		.highcharts-series-6 {
			--highcharts-graph-stroke-width: 1px;
		}

		.highcharts-legend .highcharts-color-4 .highcharts-color-3 {
			stroke-dasharray: 2 3;
		}

		.highcharts-shared-tooltip__label .highcharts-color-5 {
			stroke-dasharray: 1 2;
		}

		.highcharts-color-2 {
			stroke-dasharray: 3 5;
		}

		.highcharts-color-3 {
			stroke: none;
		}

		.highcharts-color-5 {
			stroke-dasharray: 3 5;
		}

		.highcharts-color-3 .highcharts-point {
			stroke-width: 0;
			fill: var(--highcharts-color-3);
		}

		.burn-up-chart__over-budget--planned,
		.highcharts-point.burn-up-chart__over-budget--planned {
			stroke: $red-20;
		}

		.burn-up-chart__over-budget--actual,
		.highcharts-point.burn-up-chart__over-budget--actual {
			stroke: $red-50;
		}

		.highcharts-shared-tooltip__secondary-value {
			color: $red-50;
		}

		.burn-up-chart__today-line {
			stroke: $brand-20;
		}

		.burn-up-chart__today-line-text {
			font-size: 1.375rem;
			line-height: 2rem;
			color: $brand-40;
			background-color: $neutral-00;
			padding-bottom: 0.5rem;
			text-transform: uppercase;
		}

		.burn-up-chart__budget-label-cost {
			font-weight: $scoro-fw-medium;
		}

		#burn-up-revenue-gradient-0 stop {
			stop-color: $teal-10;
		}

		#burn-up-revenue-gradient-0 stop[offset='0'] {
			stop-opacity: 1;
		}

		#burn-up-revenue-gradient-0 stop[offset='1'] {
			stop-opacity: 0;
		}

		#burn-up-revenue-gradient-1 stop[offset='1'] {
			stop-color: $purple-10;
			stop-opacity: 1;
		}

		#burn-up-revenue-gradient-1 stop[offset='0'] {
			stop-color: $teal-10;
			stop-opacity: 1;
		}

		.project_burn_up_budget_shadow {
			stroke: rgb(255 0 0 / 0%);
			fill-opacity: 1;
			fill: url(#burn-up-revenue-gradient-0);
		}

		.project_burn_up_profit_shadow {
			stroke: rgb(255 0 0 / 0%);
			fill-opacity: 1;
			fill: url(#burn-up-revenue-gradient-1);
		}

		.budget-line-cost {
			stroke: none;
		}

		.budget-line-revenue {
			stroke: none;
		}

		.burn-up-chart-legend-icon-gradient1 {
			stroke: rgb(255 0 0 / 0%);
			fill-opacity: 1;
			fill: url(#burn-up-revenue-gradient-1);
		}
	}
}
</style>
